import { CommonModule, DatePipe, JsonPipe } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, computed, Input, OnDestroy, OnInit, signal, Signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { Button } from "primeng/button";
import { DialogModule } from "primeng/dialog";
import { InputGroup } from "primeng/inputgroup";
import { InputGroupAddon } from "primeng/inputgroupaddon";
import { InputText } from "primeng/inputtext";
import { Tab, TabList, TabPanel, TabPanels, Tabs } from "primeng/tabs";
import { catchError, EMPTY, first, firstValueFrom, map, of, Subject, switchMap, takeUntil, tap, throwError } from "rxjs";
import {
  ESimPreviewService,
  GetByIccidResponse,
  isEncodedProfileESim,
  GetOrderTemplateResponse,
  isIntelligentESim,
  OrderService,
  PreviewTemplateOption
} from "@ripsim/services";
import { APIService } from "src/app/api/shared/services/api.service";
import { AppActions } from "src/app/ngrx/app.actions.types";
import { AppState } from "src/app/ngrx/app.reducer";
import { JsonViewerComponent } from "../json-viewer/json-viewer.component";
import { orderTemplateByProfile } from "./esim-lookup.ordering";
import { GeneralDialogComponent } from "../dialogs/general-dialog/general-dialog.component";
import { RIPSIM_ICONS } from "src/app/shared/models/icon-config.model";
import { RsIconComponent } from "src/app/shared/components/icons/icon.component";

interface EsimJsonViewer {
  label: string;
  json: JSON;
}
interface EsimLookupData {
  iccid: string;
  eSimProfileId: number;
  eSimProfileJson: JSON | null;
  eSimTemplateJson: JSON | null;
  jobId: string;
  jobName: string;
  jobTemplateName: string;
  updatedOnUtc: Date;
  profileHex: string;
  isOrderDeleted: boolean;
  isJobTemplateDeleted: boolean;
}

@Component({
  selector: "rs-esim-lookup",
  templateUrl: "./esim-lookup.component.html",
  styleUrl: "./esim-lookup.component.scss",
  imports: [
    CommonModule,
    DialogModule,
    GeneralDialogComponent,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
    DatePipe,
    FormsModule,
    InputGroup,
    InputGroupAddon,
    Button,
    JsonViewerComponent,
    InputText,
    RsIconComponent
  ],
  standalone: true
})
export class EsimLookupComponent implements OnInit, OnDestroy {
  public logos = {
    medium: "assets/images/logo-medium.png" // Update with actual logo path
  };
  public searchValue = "";
  public searchValueSignal = signal("");
  public isVisible = signal(false);
  public activeTab = signal(0);
  public activeSectionTab = signal(0);
  public _eSimLookup = signal({} as EsimLookupData);
  public isLoading = signal(false);

  /**
   * An Intelligent order records the variables it sent rather than a profile, so each template its
   * destination has registered is a profile the SM-DP+ can render from them. They take the place of
   * the Profile/Template tabs, and the first renders on lookup. Empty templates means the
   * destination has none registered, which is an answer rather than a failure.
   */
  protected readonly isIntelligent = signal(false);
  protected readonly previewTemplates = signal<PreviewTemplateOption[]>([]);
  protected readonly isRenderingPreview = signal(false);

  /**
   * The shown render's ASN.1 text. A stored profile's is fetched on demand; an Intelligent eSIM has
   * none stored, so the render brings its own rather than being generated a second time.
   */
  protected readonly renderedAsn1 = signal("");

  /** Renders already fetched, so moving between template tabs does not ask the SM-DP+ again. */
  private readonly previewCache = new Map<string, { json: JSON | null; hex: string; asn1: string }>();

  /** Whether the shown document is a profile, which for an Intelligent eSIM every tab is. */
  protected readonly isProfileTab = computed(() => this.isIntelligent() || this.activeTab() === 0);

  public displaySections: Signal<EsimJsonViewer[]> = computed(() => {
    const searchTerm = this.searchValueSignal()?.toLowerCase();
    return Object.entries(this._eSimLookup()[this.isProfileTab() ? "eSimProfileJson" : "eSimTemplateJson"] ?? [])
      ?.map(([, value]: [string, JSON]) => ({ label: Object.keys(value)[0], json: value }) as EsimJsonViewer)
      ?.filter((section: EsimJsonViewer) => {
        const jsonString = JSON.stringify(section.json)?.toLowerCase();
        return !searchTerm || jsonString?.includes(searchTerm);
      });
  });

  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private orderService: OrderService,
    private esimPreviewService: ESimPreviewService,
    private apiService: APIService,
    private store: Store<AppState>,
    private jsonPipe: JsonPipe
  ) {}
  @Input() dialogTitle = $localize`eSIM Viewer`;
  @Input() description = $localize`Look up an eSIM profile generated by eDGAR using an ICCID.`;
  RIPSIM_ICONS = RIPSIM_ICONS;

  ngOnInit(): void {
    this.route.queryParams
      .pipe(
        takeUntil(this.destroy$),
        tap((params) => this.isVisible.set(params["iccidLookup"]))
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Takes what the lookup answered with. Only an Intelligent eSIM has templates to render through;
   * an encoded one already has its bytes.
   */
  private applyPreviewOptions(esim: GetByIccidResponse): void {
    if (!isIntelligentESim(esim)) {
      this.clearPreview();
      return;
    }

    this.isIntelligent.set(true);
    this.previewTemplates.set(esim.templates ?? []);
  }

  /**
   * Shows the template at `index`, rendering it if this lookup has not already. The tab is moved
   * first so the strip responds while the SM-DP+ generates.
   *
   * The tab moves before the request goes out, so both outcomes check that it is still the active
   * one before drawing: renders return out of order when one template is slower than the next, and
   * neither a later answer nor a failure may land under another tab's label. A failed render clears
   * the pane rather than leaving the previous template's profile under the name of this one.
   *
   * A render is kept whatever tab is showing — it cost a real generation on the SM-DP+.
   *
   * @param announceOnSettle Reports the lookup as loaded once this render settles rather than when
   *   it starts. The first template of an Intelligent eSIM is what the operator is waiting for, so
   *   the success toast and the block UI belong to it, not to the lookup that preceded it.
   */
  protected showTemplate(index: number, announceOnSettle = false): void {
    const template = this.previewTemplates()[index];
    if (!template?.id) {
      return;
    }

    const templateId = template.id;

    this.activeTab.set(index);
    this.activeSectionTab.set(0);

    const rendered = this.previewCache.get(templateId);
    if (rendered) {
      this.applyRendered(rendered);
      if (announceOnSettle) {
        this.announceLoaded();
      }
      return;
    }

    this.isRenderingPreview.set(true);
    this.esimPreviewService
      .render({ iccid: this._eSimLookup().iccid, body: { templateId } })
      .pipe(
        first(),
        takeUntil(this.destroy$),
        catchError((error: unknown) => {
          this.store.dispatch(
            AppActions.ShowToastMessage({
              severity: "error",
              detail:
                error instanceof HttpErrorResponse && typeof error.error?.detail === "string"
                  ? error.error.detail
                  : $localize`Could not render ${this.labelOf(template)} for this eSIM.`
            })
          );
          if (this.previewTemplates()[this.activeTab()]?.id === templateId) {
            this.applyRendered({ json: null, hex: "", asn1: "" });
          }
          this.isRenderingPreview.set(false);
          if (announceOnSettle) {
            this.store.dispatch(AppActions.HideBlockUI());
          }
          return EMPTY;
        })
      )
      .subscribe((response) => {
        const result = {
          json: (response.profile ?? null) as JSON | null,
          hex: response.profileHex ?? "",
          asn1: response.profileAsn1 ?? ""
        };
        this.previewCache.set(templateId, result);
        this.isRenderingPreview.set(false);

        if (this.previewTemplates()[this.activeTab()]?.id !== templateId) {
          return;
        }

        this.applyRendered(result);
        if (announceOnSettle) {
          this.announceLoaded();
        }
      });
  }

  /** Reports the lookup as loaded: the success toast, and the block UI down. */
  private announceLoaded(): void {
    this.store.dispatch(
      AppActions.ShowToastMessage({
        severity: "success",
        detail: $localize`Successfully loaded eSIM profile.`
      })
    );
    this.store.dispatch(AppActions.HideBlockUI());
  }

  /** A template's tab label, standing in for one the SM-DP+ registered without a name. */
  protected labelOf(template: PreviewTemplateOption): string {
    return template.name ?? $localize`Untitled template`;
  }

  private applyRendered(rendered: { json: JSON | null; hex: string; asn1: string }): void {
    this.renderedAsn1.set(rendered.asn1);
    this._eSimLookup.update((curr: EsimLookupData) => ({
      ...curr,
      eSimProfileJson: rendered.json,
      profileHex: rendered.hex
    }));
  }

  private clearPreview(): void {
    this.isIntelligent.set(false);
    this.previewTemplates.set([]);
    this.isRenderingPreview.set(false);
    this.renderedAsn1.set("");
    this.previewCache.clear();
  }

  clearLookup(): void {
    this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams: null,
        replaceUrl: true
      })
      .then(() => {
        this.searchValue = "";
        this.searchValueSignal.set(this.searchValue);
        this.activeTab.set(0);
        this.activeSectionTab.set(0);
        this.isVisible.set(false);
      });
  }

  onActiveTabChange(value: string | number): void {
    if (this.isIntelligent()) {
      this.showTemplate(Number(value));
      return;
    }

    this.activeTab.set(Number(value));
    this.activeSectionTab.set(0);
  }

  onActiveSectionTabChange(value: string | number): void {
    this.activeSectionTab.set(Number(value));
  }

  /**
   * Looks the ICCID up and puts what came back on screen.
   *
   * An Intelligent order carries no eDGAR template — the SM-DP+ renders from the variables the
   * delivery sent — and `api/v2/order/{legacyId}/template` answers 422 for one, so that call is
   * skipped rather than allowed to fail the lookup. Such an eSIM also has nothing on screen until
   * its first template renders, so the render reports the lookup as loaded, not the lookup itself.
   */
  async loadEsimProfile() {
    this.activeSectionTab.set(0);
    const iccid = this._eSimLookup().iccid;
    const iccidNotFound = () => new Error(`Could not find Profile for ICCID '${iccid ?? ""}'`);
    this._eSimLookup.set({ iccid } as EsimLookupData);
    this.clearPreview();
    this.store.dispatch(AppActions.ShowBlockUI({ showSpinner: true }));
    this.esimPreviewService
      .lookup({ iccid })
      .pipe(
        first(),
        catchError(() => throwError(iccidNotFound)),
        switchMap((response: GetByIccidResponse) => {
          if (isIntelligentESim(response)) {
            return of({ response, jobTemplate: null });
          }

          return this.orderService.getOrderTemplate(response.jobId ?? 0).pipe(
            first(),
            map((jobTemplate) => ({ response, jobTemplate: jobTemplate as GetOrderTemplateResponse | null })),
            catchError((error: unknown) => {
              if (error instanceof HttpErrorResponse && error.status === 404) {
                this.applyPreviewOptions(response);
                this._eSimLookup.update((curr: EsimLookupData) => ({
                  ...curr,
                  ...EsimLookupComponent.toProfileFields(response)
                }));
              }
              return throwError(() => new Error(`Could not find Template for jobId ${response.jobId}`));
            })
          );
        }),
        tap(({ response, jobTemplate }) => {
          this.applyPreviewOptions(response);

          if (!jobTemplate) {
            this._eSimLookup.update((curr: EsimLookupData) => ({
              ...curr,
              ...EsimLookupComponent.toProfileFields(response)
            }));
            return;
          }

          if (jobTemplate.orderState === "Failure" || jobTemplate.orderState === "Canceled") {
            throw iccidNotFound();
          }
          this._eSimLookup.update((curr: EsimLookupData) => ({
            ...curr,
            ...EsimLookupComponent.toProfileFields(response),
            eSimProfileId: jobTemplate.eSimProfileId ?? 0,
            eSimTemplateJson: orderTemplateByProfile(
              isEncodedProfileESim(response) ? (response.profile ?? null) : null,
              jobTemplate.edgarTemplateJson
            ) as unknown as JSON | null,
            isOrderDeleted: jobTemplate.isOrderDeleted ?? false,
            isJobTemplateDeleted: jobTemplate.isJobTemplateDeleted ?? false
          }));
        })
      )
      .subscribe({
        next: () => {
          if (this.isIntelligent() && this.previewTemplates().length > 0) {
            this.showTemplate(0, true);
            return;
          }

          this.announceLoaded();
        },
        error: (err: Error) => {
          this.store.dispatch(
            AppActions.ShowToastMessage({
              severity: "error",
              detail: $localize`${err.message}`
            })
          );
          this.store.dispatch(AppActions.HideHttpError());
          this.store.dispatch(AppActions.HideBlockUI());
        }
      });
  }

  /**
   * Maps the profile-lookup response onto the viewer's state. Shared by the
   * happy path and the template-not-found (404) fallback, which shows the
   * profile without template data because no order row exists to carry a
   * state. Everything else maps nothing so the viewer stays empty: failed
   * and canceled orders by product decision, and any other template-call
   * failure because the order's state is unknown.
   */
  private static toProfileFields(response: GetByIccidResponse): Partial<EsimLookupData> {
    const encoded = isEncodedProfileESim(response) ? response : null;
    return {
      iccid: response.iccId ?? "",
      jobName: response.jobName ?? "",
      jobTemplateName: response.jobTemplateName ?? "",
      updatedOnUtc: new Date(response.updatedOnUtc ?? 0),
      eSimProfileJson: (encoded?.profile ?? null) as JSON | null,
      profileHex: encoded?.profileHex ?? ""
    };
  }

  copyEsimProfileJson() {
    const json = this._eSimLookup().eSimProfileJson;
    navigator.clipboard.writeText(this.jsonPipe.transform(json)).then(() =>
      this.store.dispatch(
        AppActions.ShowToastMessage({
          severity: "success",
          detail: $localize`eSIM profile in JSON format is copied to clipboard.`
        })
      )
    );
  }
  copyEsimTemplateJson() {
    const json = this._eSimLookup().eSimTemplateJson;
    navigator.clipboard.writeText(this.jsonPipe.transform(json)).then(() =>
      this.store.dispatch(
        AppActions.ShowToastMessage({
          severity: "success",
          detail: $localize`eSIM template in JSON format is copied to clipboard.`
        })
      )
    );
  }
  copyDer() {
    navigator.clipboard.writeText(this._eSimLookup().profileHex).then(() =>
      this.store.dispatch(
        AppActions.ShowToastMessage({
          severity: "success",
          detail: $localize`eSIM in DER format is copied to clipboard.`
        })
      )
    );
  }
  /**
   * Copies the profile on screen as ASN.1 text.
   *
   * An Intelligent eSIM has no stored profile to convert, so the render it is showing supplies its
   * own ASN.1 rather than the viewer asking the platform for bytes that do not exist.
   */
  copyAsn1() {
    this.isLoading.set(true);
    const asn1 = this.isIntelligent()
      ? Promise.resolve(this.renderedAsn1())
      : firstValueFrom(this.apiService.Profile_getContentById(this._eSimLookup().iccid, "avn"));
    asn1
      .then(async (avn) => await navigator.clipboard.writeText(avn))
      .then(() =>
        this.store.dispatch(
          AppActions.ShowToastMessage({
            severity: "success",
            detail: $localize`eSIM in ASN.1 format is copied to clipboard.`
          })
        )
      )
      .catch((err: DOMException) =>
        this.store.dispatch(
          AppActions.ShowToastMessage({
            severity: "error",
            detail: $localize`An error occurred when converting to ASN.1 format: ${err.message}`
          })
        )
      )
      .finally(() => this.isLoading.set(false));
  }
}
